import differenceInMonths from 'date-fns/difference_in_months'
import {
  formatPriceUnaltered,
  PLAN_SKU_THREE_MONTH_TO_ANNUAL_SPECIAL,
  PLAN_SKU_THREE_MONTH_TO_MONTHLY_SPECIAL,
  PLAN_SKU_PRICE_INCREASE_2026_ANNUAL_SPECIAL,
  PLAN_SKU_PRICE_INCREASE_2026_MONTHLY,
  PLAN_CATEGORY_ANNUAL,
  PLAN_CATEGORY_MONTHLY,
  PLAN_CATEGORY_GAIA_PLUS,
  PLAN_SKU_PRICE_INCREASE_2026_MONTHLY_CL,
  PLAN_SKU_THREE_MONTH_WITH_TRIAL_CL,
  PLAN_SKU_MONTHLY_AI_PRO,
  PLAN_SKU_TWELVE_MONTHS_TO_ANNUAL_2_SPECIAL,
  PLAN_SKU_FOURTEEN_MONTHS_TO_ANNUAL_2_SPECIAL,
} from '../../../services/plans/index'
import { USD } from '../../../services/currency/index'

/**
 * Plan SKU constants
 */
export const PLAN_SKUS = {
  THREE_MONTH_ANNUAL: PLAN_SKU_THREE_MONTH_TO_ANNUAL_SPECIAL,
  THREE_MONTH_MONTHLY: PLAN_SKU_THREE_MONTH_TO_MONTHLY_SPECIAL,
  THREE_MONTH_WITH_TRIAL: PLAN_SKU_THREE_MONTH_WITH_TRIAL_CL,
  SPECIAL_RATE: PLAN_SKU_PRICE_INCREASE_2026_ANNUAL_SPECIAL,
  MONTHLY: PLAN_SKU_PRICE_INCREASE_2026_MONTHLY_CL,
  MONTHLY_AI_PRO: PLAN_SKU_MONTHLY_AI_PRO,
  FOURTEEN_MONTHS_ANNUAL_2: PLAN_SKU_FOURTEEN_MONTHS_TO_ANNUAL_2_SPECIAL,
  TWELVE_MONTHS_ANNUAL_2: PLAN_SKU_TWELVE_MONTHS_TO_ANNUAL_2_SPECIAL,
}

/**
 * Plan configuration map
 */
export const PLAN_CONFIG = {
  [PLAN_SKUS.THREE_MONTH_ANNUAL]: {
    key: 'three-annual',
    labelKey: 'flexibleOffer',
    descriptionKey: 'threeAnnualDescription',
  },
  [PLAN_SKUS.THREE_MONTH_MONTHLY]: {
    key: 'three-monthly',
    labelKey: 'flexibleOffer',
    descriptionKey: 'threeMonthlyDescription',
  },
  [PLAN_SKUS.THREE_MONTH_WITH_TRIAL]: {
    key: 'three-trial',
    labelKey: 'flexibleOffer',
    descriptionKey: 'threeTrialDescription',
  },
  [PLAN_SKUS.SPECIAL_RATE]: {
    key: 'special',
    labelKey: 'specialLabel',
    descriptionKey: 'specialDescription',
  },
  [PLAN_SKUS.MONTHLY]: {
    key: 'monthly',
    labelKey: 'monthlyPlanLabel',
    descriptionKey: 'monthlyDescription',
  },
  [PLAN_SKUS.MONTHLY_AI_PRO]: {
    key: 'monthly-ai-pro',
    labelKey: '',
    descriptionKey: 'aiProDescription',
  },
  [PLAN_SKUS.FOURTEEN_MONTHS_ANNUAL_2]: {
    key: 'fourteen-months-annual',
    labelKey: '',
    descriptionKey: 'fourteenMonthsAnnualDescription',
  },
  [PLAN_SKUS.TWELVE_MONTHS_ANNUAL_2]: {
    key: 'twelve-months-annual',
    labelKey: '',
    descriptionKey: 'twelveMonthsAnnualDescription',
  },
}

/**
 * Get list of plan SKUs for the monthly special offer
 * @param {Map} userAccount - User account
 * @returns {Array<string>} Array of plan SKUs
 */
function getMonthlySku (userAccount) {
  const currentPlan = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan'])
  const currentPlanSku = currentPlan.get('sku') || currentPlan.get('planSku')

  // Case 1: Price = $15.99
  if (currentPlanSku === PLAN_SKU_PRICE_INCREASE_2026_MONTHLY
    || currentPlanSku === PLAN_SKU_PRICE_INCREASE_2026_MONTHLY_CL
  ) {
    return [PLAN_SKUS.THREE_MONTH_MONTHLY, PLAN_SKUS.SPECIAL_RATE]
  }

  // Case 2: in “3Mon50%off” plan’s 3Month period
  if (currentPlanSku === PLAN_SKU_THREE_MONTH_TO_MONTHLY_SPECIAL) {
    return [PLAN_SKUS.THREE_MONTH_WITH_TRIAL, PLAN_SKUS.MONTHLY]
  }

  // Case 3: Other
  return [PLAN_SKUS.THREE_MONTH_MONTHLY, PLAN_SKUS.SPECIAL_RATE, PLAN_SKUS.MONTHLY]
}

/**
 * Get list of plan SKUs for the second monthly special offer
 * @param {Map} userAccount - User account
 * @returns {Array<string>} Array of plan SKUs
 */
function getSecondMonthlySku (userAccount) {
  const currentPlan = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan'])
  const currentPlanSku = currentPlan.get('sku') || currentPlan.get('planSku')

  // Case 1: Price = $15.99
  if (currentPlanSku === PLAN_SKU_PRICE_INCREASE_2026_MONTHLY
    || currentPlanSku === PLAN_SKU_PRICE_INCREASE_2026_MONTHLY_CL
  ) {
    return [PLAN_SKUS.THREE_MONTH_MONTHLY, PLAN_SKUS.MONTHLY_AI_PRO]
  }

  // Case 2: in “3Mon50%off” plan’s 3Month period
  if (currentPlanSku === PLAN_SKU_THREE_MONTH_TO_MONTHLY_SPECIAL) {
    return [PLAN_SKUS.THREE_MONTH_WITH_TRIAL, PLAN_SKUS.MONTHLY]
  }

  // Case 3: Other
  return [PLAN_SKUS.THREE_MONTH_MONTHLY, PLAN_SKUS.MONTHLY]
}

/**
 * Get list of plan SKUs for Cancel Flow 1F monthly users (three offers).
 * Case 1: Price = $15.99 → 3Mon50%off, 119→139.99, Monthly AI Pro $19.99
 * Case 2: In 3Mon50%off period → $29.99/3mon then $39.99/3mon, 119→139.99, Monthly $15.99
 * Case 3: Other → 3Mon50%off, 119→139.99, Monthly $15.99
 * @param {Map} userAccount - User account
 * @returns {Array<string>} Array of plan SKUs
 */
function getMonthlySku1f (userAccount) {
  const currentPlan = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan'])
  const currentPlanSku = currentPlan.get('sku') || currentPlan.get('planSku')

  // Case 1: Price = $15.99
  if (currentPlanSku === PLAN_SKU_PRICE_INCREASE_2026_MONTHLY
    || currentPlanSku === PLAN_SKU_PRICE_INCREASE_2026_MONTHLY_CL
  ) {
    return [PLAN_SKUS.THREE_MONTH_MONTHLY, PLAN_SKUS.SPECIAL_RATE, PLAN_SKUS.MONTHLY_AI_PRO]
  }

  // Case 2: in "3Mon50%off" plan's 3Month period
  if (currentPlanSku === PLAN_SKU_THREE_MONTH_TO_MONTHLY_SPECIAL) {
    return [PLAN_SKUS.THREE_MONTH_WITH_TRIAL, PLAN_SKUS.SPECIAL_RATE, PLAN_SKUS.MONTHLY]
  }

  // Case 3: Other
  return [PLAN_SKUS.THREE_MONTH_MONTHLY, PLAN_SKUS.SPECIAL_RATE, PLAN_SKUS.MONTHLY]
}

// Cancel Flow 1F constants (months from offer accept date before re-offering)
const MONTHS_BEFORE_REOFFER_3_MONTH_PLANS = 14
const MONTHS_BEFORE_REOFFER_SPECIAL_RATE = 10

// New Cancel Flow 1F - base list for annual and monthly users
const PLAN_SKU_LIST_FIRST_PROMO_1F = {
  [PLAN_CATEGORY_ANNUAL]: () => [
    PLAN_SKUS.THREE_MONTH_ANNUAL,
    PLAN_SKUS.SPECIAL_RATE,
    PLAN_SKUS.MONTHLY,
  ],
  [PLAN_CATEGORY_MONTHLY]: (userAccount) => getMonthlySku1f(userAccount),
}

// Each entry is a function (userAccount) => array of plan SKUs.
const PLAN_SKU_LIST_FIRST_PROMO = {
  [PLAN_CATEGORY_ANNUAL]: () => [
    PLAN_SKUS.THREE_MONTH_ANNUAL,
    PLAN_SKUS.SPECIAL_RATE,
    PLAN_SKUS.MONTHLY,
  ],
  [PLAN_CATEGORY_MONTHLY]: (userAccount) => getMonthlySku(userAccount, true),
  [PLAN_CATEGORY_GAIA_PLUS]: () => [PLAN_SKUS.THREE_MONTH_ANNUAL, PLAN_SKUS.SPECIAL_RATE],
}

const PLAN_SKU_LIST = {
  [PLAN_CATEGORY_ANNUAL]: () => [PLAN_SKUS.THREE_MONTH_ANNUAL, PLAN_SKUS.MONTHLY],
  [PLAN_CATEGORY_MONTHLY]: (userAccount) => getSecondMonthlySku(userAccount, false),
  [PLAN_CATEGORY_GAIA_PLUS]: () => [PLAN_SKUS.THREE_MONTH_ANNUAL, PLAN_SKUS.MONTHLY],
}

/**
 * Filter Cancel Flow 1F plan SKUs based on re-offer rules (same for annual and monthly).
 * - 3Month50%off then Monthly or Annual: wait 14 months (from offer accept date) before re-offering
 * - 119 then $139.99: show again from month 10
 * - $29.99/3mon then $39.99/3mon: show this offer once (never re-offer if user took it)
 * - Hide one offer, other offers still show
 * @param {Array<string>} skuList - Base list of plan SKUs
 * @param {Map} userAccount - User account
 * @returns {Array<string>} Filtered list
 */
function filterPlanSkusFor1f (skuList, userAccount) {
  const currentPlan = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan'])
  const currentPlanSku = currentPlan.get('sku') || currentPlan.get('planSku') || ''
  const startDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'startDate'])

  const monthsSinceStart = startDate
    ? differenceInMonths(new Date(), new Date(startDate))
    : 0

  const isThreeMonthAnnualRecently = currentPlanSku === PLAN_SKU_THREE_MONTH_TO_ANNUAL_SPECIAL
    && monthsSinceStart < MONTHS_BEFORE_REOFFER_3_MONTH_PLANS
  const isThreeMonthMonthlyRecently = currentPlanSku === PLAN_SKU_THREE_MONTH_TO_MONTHLY_SPECIAL
    && monthsSinceStart < MONTHS_BEFORE_REOFFER_3_MONTH_PLANS
  const isSpecialRateRecently = currentPlanSku === PLAN_SKU_PRICE_INCREASE_2026_ANNUAL_SPECIAL
    && monthsSinceStart < MONTHS_BEFORE_REOFFER_SPECIAL_RATE
  const isThreeMonthTrialOnce = currentPlanSku === PLAN_SKU_THREE_MONTH_WITH_TRIAL_CL

  return skuList.filter((sku) => {
    if (isThreeMonthAnnualRecently && sku === PLAN_SKUS.THREE_MONTH_ANNUAL) return false
    if (isThreeMonthMonthlyRecently && sku === PLAN_SKUS.THREE_MONTH_MONTHLY) return false
    if (isSpecialRateRecently && sku === PLAN_SKUS.SPECIAL_RATE) return false
    if (isThreeMonthTrialOnce && sku === PLAN_SKUS.THREE_MONTH_WITH_TRIAL) return false
    return true
  })
}

/**
 * Monthly plan SKUs for Cancel Flow 1G special offer.
 * — During 3Mon→monthly promo period: 14MO annual2, then monthly.
 * — Otherwise: 3Mon→monthly path, then 12MO annual2.
 * @param {Map} userAccount - User account
 * @returns {Array<string>} Array of plan SKUs
 */
function getMonthlySkuCancelFlow1g (userAccount) {
  const currentPlan = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan'])
  const currentPlanSku = currentPlan.get('sku') || currentPlan.get('planSku')

  if (currentPlanSku === PLAN_SKU_THREE_MONTH_TO_MONTHLY_SPECIAL) {
    return [PLAN_SKUS.FOURTEEN_MONTHS_ANNUAL_2, PLAN_SKUS.MONTHLY]
  }

  return [PLAN_SKUS.THREE_MONTH_MONTHLY, PLAN_SKUS.TWELVE_MONTHS_ANNUAL_2]
}

/**
 * Cancel Flow 1G: SKU lists by category
 */
const PLAN_SKU_LIST_1G = {
  [PLAN_CATEGORY_ANNUAL]: () => [
    PLAN_SKUS.THREE_MONTH_ANNUAL,
    PLAN_SKUS.FOURTEEN_MONTHS_ANNUAL_2,
    PLAN_SKUS.MONTHLY,
  ],
  [PLAN_CATEGORY_MONTHLY]: (userAccount) => getMonthlySkuCancelFlow1g(userAccount),
  [PLAN_CATEGORY_GAIA_PLUS]: () => [
    PLAN_SKUS.THREE_MONTH_ANNUAL,
    PLAN_SKUS.TWELVE_MONTHS_ANNUAL_2,
    PLAN_SKUS.MONTHLY,
  ],
}

/**
 * Get list of plan SKUs for the special offer (Cancel Flow 1G).
 * @param {Map} userAccount - User account
 * @returns {Array<string>} Array of plan SKUs
 */
export function getCancelSpecialOfferSkusFor1g (userAccount) {
  const planCategory = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'planCategory']) || ''

  const getSkuList = PLAN_SKU_LIST_1G[planCategory]
  if (!getSkuList) return []
  return getSkuList(userAccount)
}

/**
 * Get list of plan SKUs for the special offer
 * @param {Map} userAccount - User account
 * @param {boolean} isInFirstPromoPeriod - Whether user is in first promotion period
 * @param {boolean} [showCancelFlow1f=false] - Whether Cancel Flow 1F is enabled for this user
 * @returns {Array<string>} Array of plan SKUs
 */
function getPlanSkuListInternal (userAccount, isInFirstPromoPeriod, showCancelFlow1f = false) {
  const planCategory = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'planCategory']) || ''

  if (showCancelFlow1f) {
    const getSkuList = PLAN_SKU_LIST_FIRST_PROMO_1F[planCategory]
    const baseList = getSkuList ? getSkuList(userAccount) : []
    return filterPlanSkusFor1f(baseList, userAccount)
  }

  const config = isInFirstPromoPeriod ? PLAN_SKU_LIST_FIRST_PROMO : PLAN_SKU_LIST
  const getSkuList = config[planCategory]

  if (!getSkuList) {
    return []
  }
  return getSkuList(userAccount)
}

/**
 * Get list of plan SKUs for the special offer
 * @param {Map} userAccount - User account
 * @param {boolean} isInFirstPromoPeriod - Whether user is in first promotion period
 * @param {boolean} [showCancelFlow1f=false] - Whether Cancel Flow 1F is enabled for this user
 * @returns {Array<string>} Array of plan SKUs
 */
export function getPlanSkuList (userAccount, isInFirstPromoPeriod, showCancelFlow1f = false) {
  return getPlanSkuListInternal(userAccount, isInFirstPromoPeriod, showCancelFlow1f)
}

// this function exists because toFixed(2) rounds to the ceiling, instead of truncating
/**
 * Truncate a number to two decimal places
 * @param {number} value - Value to truncate
 * @returns {number} Truncated value
 */
function truncateToTwoDecimals (value) {
  if (typeof value === 'number' && !Number.isInteger(value)) {
    return Math.floor(value * 100) / 100
  }
  return value
}

/**
 * Extract prices from plan by SKU
 * @param {Map} plan - Plan
 * @param {string} userLanguage - User language
 * @returns {Object} Prices map by SKU
 */
export function extractPlanPrices (plan, userLanguage) {
  const prices = {}
  const sku = plan.get('sku')
  const price = plan.get('costs', []).last() || ''
  const initialCost = parseFloat(plan.get('firstPayment'))
  const planCurrencyIso = plan.get('currencyIso') || USD

  if (sku === PLAN_SKUS.THREE_MONTH_MONTHLY) {
    prices.discountedMonthlyPrice = initialCost / 3
    prices.monthlyPrice = price
  } else if (sku === PLAN_SKUS.THREE_MONTH_ANNUAL) {
    prices.discountedMonthlyPrice = initialCost / 3
    prices.annualPrice = price
  } else if (sku === PLAN_SKUS.THREE_MONTH_WITH_TRIAL) {
    prices.discountedMonthlyPrice = initialCost / 3
    prices.quarterlyPrice = price
  } else if (sku === PLAN_SKUS.SPECIAL_RATE) {
    prices.discountedMonthlyPrice = initialCost / 12
    prices.discountedAnnualPrice = initialCost
    prices.annualPrice = price
  } else if (sku === PLAN_SKUS.MONTHLY) {
    prices.monthlyPrice = price
  } else if (sku === PLAN_SKUS.MONTHLY_AI_PRO) {
    prices.monthlyPrice = price
  } else if (sku === PLAN_SKUS.FOURTEEN_MONTHS_ANNUAL_2) {
    prices.discountedMonthlyPrice = initialCost / 14
    prices.discountedAnnualPrice = initialCost
    prices.annualPrice = price
  } else if (sku === PLAN_SKUS.TWELVE_MONTHS_ANNUAL_2) {
    prices.discountedMonthlyPrice = initialCost / 12
    prices.discountedAnnualPrice = initialCost
    prices.annualPrice = price
  }

  // Format prices
  Object.keys(prices).forEach((key) => {
    const priceValue = prices[key]
    prices[key] = formatPriceUnaltered(
      planCurrencyIso,
      truncateToTwoDecimals(priceValue),
      userLanguage,
    )
  })

  return prices
}

/**
 * Get plan offer data
 * @param {Array<Map>} plans - Plans
 * @param {Map} staticText - Static text
 * @param {string} userLanguage - User language
 * @returns {Array<Map>} Array of plan offer data
 */
export function getPlanOfferData (options) {
  const {
    plans,
    staticText,
    userLanguage,
    isInFirstPromoPeriod,
  } = options

  return plans.map((plan, index) => {
    const sku = plan.get('sku')
    const planPrices = extractPlanPrices(plan, userLanguage)

    const {
      discountedMonthlyPrice = '',
      discountedAnnualPrice = '',
      quarterlyPrice = '',
      monthlyPrice = '',
      annualPrice = '',
    } = planPrices

    const { labelKey } = PLAN_CONFIG[sku]
    let label = labelKey ? staticText.getIn(['data', labelKey]) : ''
    if (!isInFirstPromoPeriod) {
      label = index === 0 ? staticText.getIn(['data', 'bestValueOffer']) : ''
    }
    const keyModifier = PLAN_CONFIG[sku].key
    const labelModifier = isInFirstPromoPeriod ? keyModifier : 'default'
    const rawDescription = staticText.getIn(['data', PLAN_CONFIG[sku].descriptionKey]) || ''

    const description = String(rawDescription)
      .replace(/\$\{discountedMonthlyPrice\}/g, `<span class="account-cancel-special-offer__description-highlighted">${discountedMonthlyPrice}</span>`)
      .replace(/\$\{discountedAnnualPrice\}/g, `<span class="account-cancel-special-offer__description-highlighted">${discountedAnnualPrice}</span>`)
      .replace(/\$\{strikedAnnualPrice\}/g, `<span class="account-cancel-special-offer__description-striked">${annualPrice}</span>`)
      .replace(/\$\{quarterlyPrice\}/g, quarterlyPrice)
      .replace(/\$\{monthlyPrice\}/g, monthlyPrice)
      .replace(/\$\{annualPrice\}/g, annualPrice)
      .replace(/\$\{anotherYear\}/g, staticText.getIn(['data', 'anotherYear']))

    return plan
      .set('label', label)
      .set('labelModifier', labelModifier)
      .set('description', description)
      .set('keyModifier', keyModifier)
  })
}
